import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const ClientRegistration = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    phone: '',
    county: '',
    city: '',
    
    // Legal Matter
    legalCategory: '',
    urgency: '',
    description: '',
    budget: '',
    
    // Preferences
    preferredLanguage: '',
    communicationPreference: '',
    availableTimeSlots: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const counties = [
    'Nairobi', 'Mombasa', 'Kiambu', 'Nakuru', 'Kisumu', 'Uasin Gishu', 'Machakos', 'Kajiado',
    'Murang\'a', 'Nyeri', 'Kirinyaga', 'Nyandarua', 'Laikipia', 'Samburu', 'Trans Nzoia',
    'Bungoma', 'Kakamega', 'Vihiga', 'Siaya', 'Kisii', 'Nyamira', 'Migori', 'Homa Bay',
    'Turkana', 'West Pokot', 'Baringo', 'Elgeyo Marakwet', 'Nandi', 'Kericho', 'Bomet',
    'Narok', 'Kajiado', 'Makueni', 'Kitui', 'Embu', 'Tharaka Nithi', 'Meru', 'Isiolo',
    'Marsabit', 'Mandera', 'Wajir', 'Garissa', 'Tana River', 'Lamu', 'Taita Taveta', 'Kwale', 'Kilifi'
  ];

  const legalCategories = [
    'Criminal Law', 'Family Law', 'Corporate Law', 'Property Law', 
    'Employment Law', 'Contract Law', 'Immigration Law', 'Tax Law',
    'Intellectual Property', 'Personal Injury', 'Debt Recovery', 'Other'
  ];

  const urgencyLevels = [
    { value: 'immediate', label: 'Immediate (Within 24 hours)' },
    { value: 'urgent', label: 'Urgent (Within 1 week)' },
    { value: 'standard', label: 'Standard (Within 1 month)' },
    { value: 'flexible', label: 'Flexible timeline' }
  ];

  const budgetRanges = [
    'Under KES 50,000', 'KES 50,000 - 100,000', 'KES 100,000 - 250,000', 
    'KES 250,000 - 500,000', 'KES 500,000 - 1,000,000', 'Above KES 1,000,000'
  ];

  const languages = ['English', 'Swahili', 'Kikuyu', 'Luo', 'Luhya', 'Kalenjin', 'Kamba', 'Other'];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultiSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev?.[name]?.includes(value) 
        ? prev?.[name]?.filter(item => item !== value)
        : [...(prev?.[name] || []), value]
    }));
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    
    // Brief processing delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Navigate to login/register page (3rd interface) so user creates an account
    // before seeing lawyer matches (4th interface)
    navigate('/login', { state: { showRegister: true, formData } });
    setIsLoading(false);
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-text-primary mb-2">
          Personal Information
        </h2>
        <p className="text-text-secondary">
          Let's start with your basic information
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Full Name *
          </label>
          <input
            type="text"
            name="fullName"
            value={formData?.fullName}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Enter your full name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            value={formData?.email}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="your.email@example.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData?.phone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="+254 7XX XXX XXX"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            County *
          </label>
          <select
            name="county"
            value={formData?.county}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="">Select your county</option>
            {counties?.map(county => (
              <option key={county} value={county}>{county}</option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-text-primary mb-2">
            City/Town
          </label>
          <input
            type="text"
            name="city"
            value={formData?.city}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Enter your city or town"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-text-primary mb-2">
          Your Legal Matter
        </h2>
        <p className="text-text-secondary">
          Tell us about your legal issue so we can match you with the right lawyer
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Legal Category *
          </label>
          <select
            name="legalCategory"
            value={formData?.legalCategory}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="">Select legal category</option>
            {legalCategories?.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Urgency Level *
          </label>
          <select
            name="urgency"
            value={formData?.urgency}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="">Select urgency level</option>
            {urgencyLevels?.map(level => (
              <option key={level?.value} value={level?.value}>{level?.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Budget Range
          </label>
          <select
            name="budget"
            value={formData?.budget}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Select budget range (optional)</option>
            {budgetRanges?.map(range => (
              <option key={range} value={range}>{range}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Describe Your Legal Issue *
          </label>
          <textarea
            name="description"
            value={formData?.description}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Please provide details about your legal matter. The more information you provide, the better we can match you with the right lawyer."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-text-primary mb-2">
          Communication Preferences
        </h2>
        <p className="text-text-secondary">
          How would you like to communicate with your lawyer?
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Preferred Language
          </label>
          <select
            name="preferredLanguage"
            value={formData?.preferredLanguage}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Select preferred language</option>
            {languages?.map(language => (
              <option key={language} value={language}>{language}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Communication Method
          </label>
          <select
            name="communicationPreference"
            value={formData?.communicationPreference}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Select communication method</option>
            <option value="email">Email</option>
            <option value="phone">Phone calls</option>
            <option value="video">Video calls</option>
            <option value="in-person">In-person meetings</option>
            <option value="platform">Platform messaging</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Available Time Slots (Optional)
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {['Morning (8-12)', 'Afternoon (12-17)', 'Evening (17-20)', 'Weekends', 'Flexible', 'Emergency only']?.map(slot => (
              <label key={slot} className="flex items-center space-x-2 p-3 border border-border rounded-lg hover:bg-secondary-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData?.availableTimeSlots?.includes(slot)}
                  onChange={() => handleMultiSelectChange('availableTimeSlots', slot)}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
                <span className="text-sm text-text-secondary">{slot}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Scale" size={20} color="white" />
              </div>
              <span className="text-xl font-heading font-bold text-primary">LawDesk KE</span>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-text-secondary hover:text-text-primary transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-surface border-b border-border px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-primary">Step {currentStep} of 3</span>
            <span className="text-sm text-text-secondary">{Math.round((currentStep / 3) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-secondary-100 rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className="flex items-center space-x-2 px-6 py-3 text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
              >
                <Icon name="ChevronLeft" size={20} />
                <span>Previous</span>
              </button>

              {currentStep === 3 ? (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center space-x-2 bg-primary text-white px-8 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
                >
                  {isLoading ? (
                    <>
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span>Finding Lawyers...</span>
                    </>
                  ) : (
                    <>
                      <span>Find My Lawyer</span>
                      <Icon name="Search" size={20} />
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-smooth"
                >
                  <span>Next</span>
                  <Icon name="ChevronRight" size={20} />
                </button>
              )}
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ClientRegistration;